import { useContext, useMemo, useState } from 'react';

import { Button } from '@mui/material';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { useNavigate } from 'react-router';
import { processOrderRequest, processSubscriptionOrder, stripeCreateCheckoutSession } from '../api/payment';
import StripeLogo from "../assets/stripe-logo.svg";
import { AccountContext } from '../context/account';
import { CartItem } from '../core/global';
import { getBasketCost } from '../helpers/cart';
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/cart-modal.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { CartPurchaseCostSummary, CartPurchaseTokensCostSummary } from './cart-purchase-cost-summary';
import { CartTokensModal } from './cart-tokens-modal';
import { ErrorMessage } from './error-message';
import { Img } from './image';
import Loader from './loader';
import { Textarea } from './mui-styled-textarea';
import { PurchaseTokensModal } from './purchase-tokens-modal';

export interface PaymentProviderProps {
  data: CartItem[]
  currentAdressBookId: number | string | null
  onClose: () => void
}

export const PaymentProvider = ({
  data,
  currentAdressBookId,
  onClose,
}: PaymentProviderProps) => {
  const [notes, setNotes] = useState<string | null>(null)
  const [processingOrder, setProcessingOrder] = useState(false)
  const [showPayWithTokensModal, setShowPayWithTokensModal] = useState(false)
  const [showTokensModal, setShowTokensModal] = useState(false)

  const {
    account,
    isVatExempt,
    purchaseTokens,
    paymentType,
    userTokensTotal,
    userActivePaymentMethod,
    cartError,
    setCartError,
    refetchCart,
    refetchProjects,
    setTransaction,
    refetchCompanyRequests,
  } = useContext(AccountContext);
  

  const {
    subtotal,
  } = useMemo(() => {
    return getBasketCost( data, isVatExempt, paymentType )
  }, [data, isVatExempt])

  const isExternalAddress = useMemo(() => {
    if (userActivePaymentMethod 
      && (
          userActivePaymentMethod?.type === "company-tokens" 
          || userActivePaymentMethod?.type === "subscription"
          || userActivePaymentMethod?.type === "request-approval"
          || userActivePaymentMethod?.type === "company-stripe" 
          || userActivePaymentMethod?.type === "company-po"
        )
      && userActivePaymentMethod?.config) {
        return true
    }
    return false
  }, [userActivePaymentMethod])

  const useOrganisationId = useMemo(() => {
    if (isExternalAddress && userActivePaymentMethod?.config) {
        return userActivePaymentMethod?.config.company_id
    }
    if (userActivePaymentMethod?.type === "stripe" && userActivePaymentMethod?.config) {
      return userActivePaymentMethod?.config.company_id
    }
    if (account) {
      const {organisation, uid} = account
      if (uid && organisation && Array.isArray(organisation)) {
        const found = organisation.find( org => {
          return org.created_by === uid || org.role === 'admin' || org.role === 'super-admin'
        })
        if (found) {
          return found?.id || null
          
        }
      }
    }
    return null
  }, [account, isExternalAddress, userActivePaymentMethod])


  const totalPurchaseTokens = useMemo(() => {
    return purchaseTokens.map( t => t.tokens).reduce((prev, next) => prev + next, 0)
  }, [purchaseTokens])

  const navigate = useNavigate();

  if (processingOrder) {
    return (
      <div className={styles.processing}>
        <Loader showCog title='Processing order...' center />
      </div>
    )
  }

  if (paymentType === "subscription") {
    return (
      <>
        <ErrorMessage message={cartError} />
        <CartPurchaseCostSummary fullTable />
        <div className={styles.tokenSummary}>
          <Button
            id="acquire-data-button"
            color="success"
            variant="contained"
            onClick={() => {
              setProcessingOrder(true)
              trackEvent("shopping-cart-purchase", {payment_method: paymentType, organisation_id: useOrganisationId,})
              const executeOrder = async () => {
                const result = await processSubscriptionOrder({
                  payment_method: paymentType,
                  processing_token: nanoid(),
                  organisation_id: useOrganisationId,
                  address_book_id: isExternalAddress ? null : (currentAdressBookId && typeof currentAdressBookId === 'number')? currentAdressBookId : null,
                })
                refetchCart()
                refetchProjects()
                if (result) {
                  const {response, error, message} = result
                  if (error !== 200) {
                    setCartError([message] as string[])
                  } else {
                    const {order_details} = response
                    if (order_details) {
                      const { status, processing_token, transaction_id } = order_details
                      setTransaction({
                        type: "subscription",
                        isPaymentSuccess: !!(status && status === 'processed'),
                        processingToken: `${processing_token}`,
                        transactionId: `${transaction_id}`,
                        tokens: null,   // only set to number when tokens are purchased
                        isRedirect: false,
                      })  
                    } else {
                      setCartError(['Cannot retrieve order details while processing order.'] as string[])
                    }
                    onClose()
                    refetchCompanyRequests();
                  }
                }
                setProcessingOrder(false)
              }
              executeOrder()
              setCartError([])
            }}>
              Acquire data
          </Button>
        </div>
      </>
    )
  }
  if (purchaseTokens.length > 0) {
    return (
      <>
        <CartPurchaseTokensCostSummary fullTable />
        <div className={styles.tokenSummary}>
          <div>
            <h4>Your current tokens balance</h4>
            <table className={tableStyles.table}>
              <tfoot>
                <tr>
                  <th>Your current tokens value:</th>
                  <td className={tableStyles.currency}>{numeral(userTokensTotal).format("0,0")}</td>
                </tr>
                <tr>
                  <th>Tokens to purchase:</th>
                  <td className={tableStyles.currency}>{numeral(totalPurchaseTokens).format("0,0")}</td>
                </tr>
                <tr>
                  <th>Tokens after purchase:</th>
                  <td className={tableStyles.currency}>{numeral(userTokensTotal + purchaseTokens.map( t => t.tokens).reduce((prev, next) => prev + next, 0)).format("0,0")}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </>
    )
  }

  if (paymentType === "po") {
    return (
      <>
        <CartPurchaseCostSummary fullTable />
        <div className={styles.tokenSummary}>
          <div>
            <h4>Purchase Order</h4>
            <p>Marking payment with purchase order...</p> 
          </div>
          <Button
            color="success"
            variant="contained"
            onClick={() => {
              // setShowTokensModal(true)
              // setShowPayWithTokensModal(true)
            }}>
              Create Purchase Order 
          </Button>
        </div>
      </>
    )
  }

  if (paymentType === "tokens") {
    return (
      <>
        <CartPurchaseCostSummary fullTable />
        <div className={styles.tokenSummary}>
          <div>
            <h4>Pay using your token balance</h4>
            <p>Making a payment with tokens is the most cost-effective way to purchase MetOceanWorks data. Tokens offer flexibility and can be purchased in bulk, allowing you to save money while accessing the data you need efficiently.</p> 
            <table className={tableStyles.table}>
              <tfoot>
                <tr>
                  <th>Your available tokens:</th>
                  <td className={tableStyles.currency}>{numeral(userTokensTotal).format("0,0")}</td>
                </tr>
                <tr>
                  <th>This purchase:</th>
                  <td className={tableStyles.currency}>{numeral(subtotal/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR) - Math.ceil(subtotal/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR)*0.10)).format("0,0")}</td>
                </tr>
                <tr>
                  <th>Tokens after purchase:</th>
                  <td className={tableStyles.currency}>{numeral(userTokensTotal - (subtotal/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR) - Math.ceil(subtotal/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR)*0.10))).format("0,0")}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Button
            color="success"
            variant="contained"
            onClick={() => {
              setShowTokensModal(true)
              // setShowPayWithTokensModal(true)
            }}>
              Pay with tokens
          </Button>
        </div>
      </>
    )
  }

  if (userActivePaymentMethod && userActivePaymentMethod?.type === "request-approval" && userActivePaymentMethod?.config) {
    const {
      name
    } = userActivePaymentMethod.config
    return (
      <>
        <ErrorMessage message={cartError} />
        <CartPurchaseCostSummary fullTable />
        <div className={styles.stripeSummary}>
          <div>
            <h4>Request approval</h4>
            <p>You are requesting {name} to complete and acquire this data. Once the transaction is processed and data becomes available, you will be notified and the purchased data will show in your project list.</p>
            <Textarea
              placeholder="Please add any notes for the request here... (optional)"
              value={notes || ''}
              onChange={(value: string) => {
                setNotes(value)
              }}
            />
            <Button
              color="success"
              variant="contained"
              onClick={() => {
                setProcessingOrder(true)
                const executeOrder = async () => {
                  const result = await processOrderRequest({
                    processing_token: nanoid(),
                    organisation_id: useOrganisationId,
                    notes,
                  })
                  if (result) {
                    const {response, error, message} = result
                    if (error !== 200) {
                      setCartError([message] as string[])
                    } else {
                      const {success} = response
                      refetchCart()
                      refetchProjects()                 
                      setTransaction({
                        type: "request-approval",
                        isPaymentSuccess: true,
                        processingToken: null,
                        transactionId: null,
                        tokens: null,   // only set to number when tokens are purchased
                        isRedirect: false,
                        isPurchaseRequest: true,
                      })
                      onClose()
                      refetchCompanyRequests();
                    }
                  }
                  setProcessingOrder(false)
                }
                executeOrder()
                setCartError([])
              }}>
                Request data
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <ErrorMessage message={cartError} />
      <CartPurchaseCostSummary fullTable />
      <div className={styles.stripeSummary}>
        <div>
          <h4>Pay using Stripe</h4>
          <p>Making a payment with Stripe is quick, secure, and user-friendly. Stripe allows you to pay using various methods, including credit or debit cards, digital wallets like Apple Pay or Google Pay, and even local payment options depending on your region. </p>
        </div>
        <Button
          className={styles.stripeButton}
          endIcon={<Img src={StripeLogo} alt='logo' width={40} />}
          color="success"
          variant="contained"
          onClick={async () => {
            const result = await stripeCreateCheckoutSession({
              tokens: totalPurchaseTokens || null,
              organisation_id: useOrganisationId,
              processing_token: nanoid(), 
              payment_method: 'stripe', 
              project_id: null,
              purchase_tokens: purchaseTokens,
              address_book_id: currentAdressBookId,
            })
            const {response, error} = result
            if (response) {
              const {session} = response
              if (session && session?.url) {
                navigate(session?.url)
              }  
            } else {
              const {message} = result
              setCartError(['Your purchase cannot be processed. Could not create **Stripe** checkout session. ', message] as string[])
            }
          }}>
            Pay with
        </Button>
      </div>

      {showPayWithTokensModal && <CartTokensModal 
        onClose={() => {
          setShowPayWithTokensModal(false)
        }}
        onConfirm={() => {
          setShowPayWithTokensModal(false)
        }}
      />}

      {showTokensModal && <PurchaseTokensModal
        intialCustomTokens={21}
        onClose={() => {
          setShowTokensModal(false)
        }} 
      />}
    </>
  )
}
